// src/context/DocumentPicker.jsx
//
// The document picker shared by the personalization form and the persona assist forms (v197; born in v196 as
// the personalization form's own). A request reads ONLY the files it names (never "every document"), so this is
// a picker, not the KB library:
//   • this visit's uploads on top, TICKED (up to the cap); then the workspace's other documents UNTICKED,
//     ALPHABETICAL (the list API has no upload date) — documentUpload.orderPickerRows;
//   • at most MAX_LISTED_DOCUMENTS ticked; a dot-leading name is shown but never tickable (requests refuse it);
//   • the checkbox only ticks — ⛔ no delete / reindex / download (that is the Knowledge Base page);
//   • uploads go through documentUpload.uploadSequentially (one per file, stop at a billing refusal), with the
//     same replace confirmation the KB library asks for (the backend overwrites silently).
// `label` is the list's accessible name (each caller passes its own heading).
// ⛔ Every failure is OUR sentence (documentPickerCopy.uploadErrorCopy / RunRefusalNotice) — never the server's text.
// ⛔ No indexing wait: requests read each file from storage whatever its index status.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../admin/components/ConfirmDialog.jsx';
import RunRefusalNotice from '../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../utils/runRefusal.js';
import { listDocuments, uploadDocument } from './contextApi.js';
import {
  ACCEPT, namesToReplace, uploadSequentially,
  MAX_LISTED_DOCUMENTS, PICKER_SEARCH_THRESHOLD, isSelectableFilename, isTextless, orderPickerRows,
} from './documentUpload.js';
import { uploadErrorCopy } from './documentPickerCopy.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

// Local copies of the three control styles this picker uses (it imports nothing from a feature folder).
const HINT = { fontSize: '12px', color: COLORS.textTertiary, margin: '6px 0 0', lineHeight: 1.5 };
const SECONDARY = {
  height: '46px', padding: '0 18px', borderRadius: RADII.control, border: `1px solid ${COLORS.borderStrong}`,
  background: COLORS.surface, color: COLORS.ink, fontFamily: FONT_SANS, fontSize: '14px', fontWeight: 600,
  cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
};
const NOTE = (sem) => ({
  padding: '12px 14px', borderRadius: RADII.control, background: sem.bg, color: sem.fg,
  fontFamily: FONT_SANS, fontSize: '13px', lineHeight: 1.55, margin: '0 0 16px',
});

const BOX = {
  maxHeight: '320px', overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: RADII.control,
  background: COLORS.surface,
};
const ROW = (dim) => ({
  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
  borderTop: `1px solid ${COLORS.border}`, fontFamily: FONT_SANS, fontSize: '13px',
  color: dim ? COLORS.textTertiary : COLORS.ink,
});
const TAG = (sem) => ({
  padding: '1px 8px', borderRadius: RADII.pill, background: sem.bg, color: sem.fg, fontSize: '11px', fontWeight: 600,
});
const SEARCH = {
  width: '100%', boxSizing: 'border-box', height: '38px', padding: '0 12px', margin: '0 0 8px',
  borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, fontFamily: FONT_SANS, fontSize: '13px',
};

export default function DocumentPicker({
  tenantId, selected, onChange, label, disabled = false, reloadToken = 0, onBusyChange, onDocsChange,
}) {
  const { t } = useTranslation('context');
  // keyPrefix keeps the replace dialog's three keys short (and its body key out of the no-server-text grep gate).
  const { t: tr } = useTranslation('context', { keyPrefix: 'documents.confirmReplace' });
  const [docs, setDocs] = useState(null);           // null = loading
  const [loadFailed, setLoadFailed] = useState(false);
  const [uploadedNow, setUploadedNow] = useState([]); // most recent first
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);       // [{filename, text}]
  const [refusal, setRefusal] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // The callbacks are read through refs so an inline handler in the parent never re-lists.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onDocsChangeRef = useRef(onDocsChange);
  onDocsChangeRef.current = onDocsChange;

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const data = await listDocuments({ tenant: tenantId });
      const list = Array.isArray(data?.documents) ? data.documents : [];
      setDocs(list);
      if (onDocsChangeRef.current) onDocsChangeRef.current(list);   // e.g. a form's "all ticked files textless" hint
      // A ticked file that is no longer stored is unticked (e.g. after a 422 DOCUMENT_NOT_FOUND).
      const names = new Set(list.map((d) => d.filename));
      const kept = selectedRef.current.filter((n) => names.has(n));
      if (kept.length !== selectedRef.current.length) onChangeRef.current(kept);
    } catch {
      setDocs((prev) => prev || []);
      setLoadFailed(true);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load, reloadToken]);
  useEffect(() => { if (onBusyChange) onBusyChange(busy); }, [busy, onBusyChange]);

  const rows = useMemo(() => orderPickerRows(docs || [], uploadedNow), [docs, uploadedNow]);
  const q = query.trim().toLowerCase();
  const visible = q ? rows.filter((d) => d.filename.toLowerCase().includes(q)) : rows;
  const atCap = selected.length >= MAX_LISTED_DOCUMENTS;

  const toggle = (name) => {
    if (selected.includes(name)) onChange(selected.filter((n) => n !== name));
    else if (!atCap) onChange([...selected, name]);
  };

  async function runUploads(files) {
    setBusy(true);
    setProblems([]);
    setRefusal(null);
    const results = await uploadSequentially({
      files,
      upload: (file) => uploadDocument({ tenant: tenantId, file }),
      isRefusal: refusalFromError,
      onFailure: (_file, _err, r) => { if (r) setRefusal(r); },
    });
    const uploaded = results.filter((r) => r.ok).map((r) => r.filename);
    if (uploaded.length) {
      setUploadedNow((prev) => [...uploaded.slice().reverse(), ...prev.filter((n) => !uploaded.includes(n))]);
      // Tick the new files up to the cap (a re-uploaded, already ticked name stays ticked once).
      const room = Math.max(0, MAX_LISTED_DOCUMENTS - selectedRef.current.length);
      const toTick = uploaded.filter((n) => isSelectableFilename(n) && !selectedRef.current.includes(n)).slice(0, room);
      if (toTick.length) onChange([...selectedRef.current, ...toTick]);
    }
    setProblems(results.filter((r) => !r.ok && !r.refusal).map((r) => ({
      filename: r.file.name,
      text: r.skipped ? t('documents.outcomes.skipped')
        : r.problem ? t(`documents.validation.${r.problem.key}`, r.problem.params)
          : uploadErrorCopy(t, r.error),
    })));
    setBusy(false);
    await load();
  }

  function pick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const names = namesToReplace(files, docs || []);
    if (names.length) { setConfirmReplace({ files, names }); return; }
    runUploads(files);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <button type="button" style={SECONDARY} disabled={disabled || busy} onClick={() => inputRef.current?.click()}>
          {busy ? t('documents.picker.uploading') : t('documents.picker.upload')}
        </button>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} onChange={pick} style={{ display: 'none' }}
          data-testid="picker-file-input" />
        <span style={HINT}>{t('documents.picker.selected', { count: selected.length, max: MAX_LISTED_DOCUMENTS })}</span>
        {/* At the cap the reason sits HERE, beside the counter and in the warning colour — under the scroll box,
            in hint grey, it went unseen (acceptance 2026-10-02, point 5). */}
        {atCap && (
          <span role="status" style={{ ...HINT, color: SEMANTIC.attenzione.fg, fontWeight: 600 }}>
            {t('documents.picker.limit', { max: MAX_LISTED_DOCUMENTS })}
          </span>
        )}
      </div>

      {refusal && <div style={{ marginBottom: '10px' }}><RunRefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} /></div>}
      {problems.length > 0 && (
        <div role="alert" style={NOTE(SEMANTIC.errore)}>
          {problems.map((p) => <div key={p.filename}><strong>{p.filename}</strong>: {p.text}</div>)}
        </div>
      )}
      {loadFailed && (
        <div role="alert" style={NOTE(SEMANTIC.attenzione)}>
          {t('documents.picker.loadFailed')}{' '}
          <button type="button" style={{ ...SECONDARY, height: '30px' }} onClick={load}>{t('documents.picker.reload')}</button>
        </div>
      )}

      {rows.length > PICKER_SEARCH_THRESHOLD && (
        <input type="search" aria-label={t('documents.picker.search')} placeholder={t('documents.picker.search')}
          value={query} onChange={(e) => setQuery(e.target.value)} style={SEARCH} />
      )}

      {docs === null ? (
        <p role="status" style={HINT}>…</p>
      ) : rows.length === 0 ? (
        <p style={HINT}>{t('documents.picker.empty')}</p>
      ) : (
        <div style={BOX} role="group" aria-label={label}>
          {visible.map((d, i) => {
            const checked = selected.includes(d.filename);
            const selectable = isSelectableFilename(d.filename);
            const capped = !checked && atCap;
            const off = disabled || !selectable || capped;
            return (
              <label key={d.filename} style={{ ...ROW(!selectable), ...(i === 0 ? { borderTop: 'none' } : null) }}
                title={!selectable ? t('documents.picker.dotName') : capped ? t('documents.picker.limit', { max: MAX_LISTED_DOCUMENTS }) : undefined}>
                <input type="checkbox" checked={checked} disabled={off} onChange={() => toggle(d.filename)} />
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{d.filename}</span>
                {uploadedNow.includes(d.filename) && <span style={TAG(SEMANTIC.info)}>{t('documents.picker.thisVisit')}</span>}
                {isTextless(d) && <span style={TAG(SEMANTIC.attenzione)} title={t('documents.noTextHint')}>{t('documents.noText')}</span>}
              </label>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmReplace}
        onClose={() => setConfirmReplace(null)}
        onConfirm={() => { const { files } = confirmReplace; runUploads(files); }}
        title={tr('title')}
        message={tr('message', { names: (confirmReplace?.names || []).join('", "') })}
        confirmLabel={tr('confirm')}
        variant="danger"
      />
    </div>
  );
}
